/**
 * OCTOPUS — TankConfig (Phase 999.4 Plan 04 — D-06/D-07/D-04 + multi-tanque D-14)
 *
 * Aba "Tanques" do diálogo Editar Equipamento. Evolução multi-tanque (achado da
 * validação D-14 em campo, 2026-07-10: DRA-0006 tem tanque PRINCIPAL + FILTRADO):
 * lista de tanques com label editável, capacidade (L) e paths de sensor por
 * tanque. O PRIMEIRO tanque é o PRINCIPAL — o backend espelha os campos dele
 * nos campos legados que detector/timeline/saldo leem (evolução completa por
 * tanque = backlog 999.16). Reserva mínima e detector permanecem por
 * EQUIPAMENTO (o detector é alimentado pelo sensor do tanque principal).
 *
 * Paths de sensor: seleção COM BUSCA (achado do D-14 — campo aberto é
 * semi-impossível de acertar) alimentada por `GET /api/fuel/sensor-paths`
 * (fields reais do equipamento no Influx, 30d). Entrada manual permanece como
 * fallback quando a relação não carrega ou o path ainda não emitiu.
 *
 * Borda de UI (regra do módulo de combustível): o dono informa CAPACIDADE e
 * RESERVA em LITROS; a conversão L↔m³ é do backend (ADR-2026-10-25) —
 * o schema interno permanece em m³ ponta a ponta no backend. INV-UI-TRAIL-001:
 * este componente só edita config; nunca computa saldo ou recomputa telemetria.
 *
 * Estado vazio "sem sensor" (D-07): sem sensor no tanque PRINCIPAL, o detector
 * fica inativo por definição. A seção do detector NUNCA é ocultada.
 *
 * D-04: esta aba NÃO coleta quem recebe/como entrega alerta — isso é do
 * domínio Alertas (central de alertas), fora do escopo de config de tanque.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Loader2, Trash2, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { showErrorToast } from '@/utils/errorHandler';
import { fuelApi } from '@/services/fuelApi';

// Forma estrita (ADR-2026-10-25): a conversão L↔m³ é do BACKEND (registry).
// A tela recebe `*_l` no GET e envia `*_l` no PUT — só valida o número digitado.
const lText = (l) => (l == null ? '' : String(Math.round(l)));
const lValue = (l) => {
  const n = Number(l);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const MAX_TANKS = 8;

const emptyTank = () => ({
  id: null,
  label: '',
  capacityL: '',
  sensorLevelPath: '',
  sensorCapacityPath: '',
});

/**
 * Seleção com busca para path de sensor (Popover + cmdk). `options` vem da
 * relação real do Influx; o valor digitado pode ser usado como path custom
 * (fallback quando a relação está indisponível ou o sensor ainda não emitiu).
 */
const PathCombobox = ({ value, onChange, options, placeholder, testId, disabled }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const showCustom = query.trim() && !options.includes(query.trim());

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(''); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
          data-testid={testId}
        >
          <span className={cn('truncate text-left', !value && 'text-muted-foreground')}>
            {value || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Buscar sensor..."
            value={query}
            onValueChange={setQuery}
          />
          <CommandList className="max-h-56">
            <CommandEmpty>Nenhum sensor encontrado.</CommandEmpty>
            {value && (
              <CommandGroup>
                <CommandItem
                  value="__clear__"
                  onSelect={() => { onChange(''); setOpen(false); }}
                  className="text-muted-foreground"
                >
                  Limpar seleção
                </CommandItem>
              </CommandGroup>
            )}
            <CommandGroup heading="Sensores do equipamento (Influx, 30d)">
              {options.map((p) => (
                <CommandItem
                  key={p}
                  value={p}
                  onSelect={() => { onChange(p); setOpen(false); }}
                >
                  <Check className={cn('mr-2 h-4 w-4', value === p ? 'opacity-100' : 'opacity-0')} />
                  <span className="truncate">{p}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            {showCustom && (
              <CommandGroup heading="Manual">
                <CommandItem
                  value={`__custom__${query}`}
                  onSelect={() => { onChange(query.trim()); setOpen(false); }}
                >
                  Usar "{query.trim()}"
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const TankConfig = ({ equipmentId }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tanks, setTanks] = useState([emptyTank()]);
  const [reserveL, setReserveL] = useState('');
  const [detectorActive, setDetectorActive] = useState(false);
  const [minRefuelL, setMinRefuelL] = useState('');
  const [windowMin, setWindowMin] = useState('');
  const [sensorPaths, setSensorPaths] = useState([]);
  const [pathsSource, setPathsSource] = useState('loading');

  const fetchConfig = useCallback(async () => {
    if (!equipmentId) return;
    try {
      setLoading(true);
      const res = await fuelApi.getFuelConfig(equipmentId);
      const data = res.data || {};
      const list = Array.isArray(data.tanks) && data.tanks.length > 0
        ? data.tanks
        : [{
            id: 'main',
            label: 'Principal',
            capacity_l: data.tank_capacity_l,
            sensor_level_path: data.sensor_level_path,
            sensor_capacity_path: data.sensor_capacity_path,
          }];
      setTanks(list.map((t) => ({
        id: t.id || null,
        label: t.label || '',
        capacityL: lText(t.capacity_l),
        sensorLevelPath: t.sensor_level_path || '',
        sensorCapacityPath: t.sensor_capacity_path || '',
      })));
      setReserveL(lText(data.reserve_threshold_l));
      setDetectorActive(!!data.detector?.active);
      setMinRefuelL(data.detector?.min_refuel_l != null ? String(data.detector.min_refuel_l) : '');
      setWindowMin(data.detector?.window_min != null ? String(data.detector.window_min) : '');
    } catch (error) {
      showErrorToast(toast, error, 'Erro ao carregar a configuração de tanque.');
    } finally {
      setLoading(false);
    }
  }, [equipmentId]);

  const fetchSensorPaths = useCallback(async () => {
    if (!equipmentId) return;
    try {
      const res = await fuelApi.getSensorPaths(equipmentId);
      setSensorPaths(res.data?.paths || []);
      setPathsSource(res.data?.source || 'error');
    } catch {
      setSensorPaths([]);
      setPathsSource('error');
    }
  }, [equipmentId]);

  useEffect(() => { fetchConfig(); fetchSensorPaths(); }, [fetchConfig, fetchSensorPaths]);

  // Sugestão: paths de tanque primeiro (tanks.*), depois o resto — a busca do
  // combobox cobre qualquer outro sensor.
  const orderedPaths = useMemo(() => {
    const tankish = sensorPaths.filter((p) => p.toLowerCase().startsWith('tank'));
    const rest = sensorPaths.filter((p) => !p.toLowerCase().startsWith('tank'));
    return [...tankish, ...rest];
  }, [sensorPaths]);

  const updateTank = (idx, patch) => {
    setTanks((prev) => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  };

  const addTank = () => {
    setTanks((prev) => (prev.length >= MAX_TANKS ? prev : [...prev, emptyTank()]));
  };

  const removeTank = (idx) => {
    setTanks((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Payload só de config de tanque/detector — nada do domínio Alertas
      // entra aqui (D-04). O backend espelha tanks[0] nos campos legados.
      const payload = {
        reserve_threshold_l: lValue(reserveL),
        detector: {
          active: detectorActive,
          min_refuel_l: minRefuelL !== '' ? Number(minRefuelL) : null,
          window_min: windowMin !== '' ? Number(windowMin) : null,
        },
        tanks: tanks.map((t, i) => ({
          id: t.id || (i === 0 ? 'main' : null),
          label: t.label.trim() || null,
          capacity_l: lValue(t.capacityL),
          sensor_level_path: t.sensorLevelPath.trim() || null,
          sensor_capacity_path: t.sensorCapacityPath.trim() || null,
        })),
      };
      await fuelApi.putFuelConfig(equipmentId, payload);
      toast.success('Configuração de tanques salva.');
      await fetchConfig();
    } catch (error) {
      showErrorToast(toast, error, 'Erro ao salvar a configuração de tanque.');
    } finally {
      setSaving(false);
    }
  };

  const mainHasSensor = !!(tanks[0]?.sensorLevelPath || '').trim();

  return (
    <div className="space-y-4" data-testid="tank-config">
      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Carregando configuração de tanque...
        </div>
      )}

      {tanks.map((tank, idx) => (
        <div key={idx} className="space-y-3 border rounded-lg p-3" data-testid={`tank-card-${idx}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-1">
              <Input
                className="max-w-56 h-8"
                placeholder={idx === 0 ? 'Principal' : `Tanque ${idx + 1}`}
                value={tank.label}
                onChange={(e) => updateTank(idx, { label: e.target.value })}
                data-testid={`tank-label-${idx}`}
              />
              {idx === 0 && (
                <Badge variant="secondary" className="shrink-0">
                  Principal — alimenta o detector e o histórico
                </Badge>
              )}
            </div>
            {idx > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive"
                onClick={() => removeTank(idx)}
                data-testid={`tank-remove-${idx}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor={`tank-capacity-l-${idx}`}>Capacidade total (L)</Label>
              <Input
                id={`tank-capacity-l-${idx}`}
                type="number"
                min="0"
                step="1"
                value={tank.capacityL}
                onChange={(e) => updateTank(idx, { capacityL: e.target.value })}
                data-testid={`tank-capacity-l-${idx}`}
              />
            </div>
            {idx === 0 && (
              <div className="space-y-1">
                <Label htmlFor="tank-reserve-l">Reserva mínima (L)</Label>
                <Input
                  id="tank-reserve-l"
                  type="number"
                  min="0"
                  step="1"
                  value={reserveL}
                  onChange={(e) => setReserveL(e.target.value)}
                  data-testid="tank-reserve-l"
                />
              </div>
            )}
          </div>

          {/* Capacidade é SEMPRE o valor manual acima (decisão do dono
              2026-07-10) — sensor de capacidade removido da UI por ser
              misleading: nenhum leitor do core o consome. */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Sensor de nível (opcional)</Label>
              <PathCombobox
                value={tank.sensorLevelPath}
                onChange={(v) => updateTank(idx, { sensorLevelPath: v })}
                options={orderedPaths}
                placeholder="Selecionar sensor de nível..."
                testId={`tank-sensor-level-path-${idx}`}
              />
            </div>
          </div>
        </div>
      ))}

      {pathsSource !== 'influx' && pathsSource !== 'loading' && (
        <p className="text-xs text-muted-foreground">
          Relação de sensores indisponível no momento — use a opção "Manual" na
          busca para digitar o path.
        </p>
      )}

      {!mainHasSensor && (
        <div
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800"
          data-testid="tank-no-sensor-warning"
        >
          <TriangleAlert className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Sem sensor de nível no tanque principal — o detector fica inativo; o
            saldo opera por lançamentos + telemetria de consumo.
          </span>
        </div>
      )}

      <div className="space-y-3 border rounded-lg p-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium">Detector de abastecimento (tanque principal)</Label>
          <Switch
            checked={detectorActive}
            onCheckedChange={setDetectorActive}
            data-testid="tank-detector-active"
          />
        </div>
        {detectorActive && !mainHasSensor && (
          <p className="text-xs text-amber-700">
            Ativo sem sensor de nível fica inerte — nenhum salto será detectado até um sensor ser selecionado.
          </p>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="tank-detector-min-refuel-l">Salto mínimo (L)</Label>
            <Input
              id="tank-detector-min-refuel-l"
              type="number"
              min="0"
              step="1"
              value={minRefuelL}
              onChange={(e) => setMinRefuelL(e.target.value)}
              data-testid="tank-detector-min-refuel-l"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="tank-detector-window-min">Janela (min)</Label>
            <Input
              id="tank-detector-window-min"
              type="number"
              min="1"
              max="60"
              step="1"
              value={windowMin}
              onChange={(e) => setWindowMin(e.target.value)}
              data-testid="tank-detector-window-min"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <div className="space-y-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addTank}
            disabled={tanks.length >= MAX_TANKS}
            data-testid="tank-add"
          >
            + Adicionar tanque
          </Button>
          <p className="text-xs text-muted-foreground">
            O tanque principal alimenta o detector e o histórico; a evolução por
            tanque (detector/alertas individuais) chega na reavaliação 999.16.
          </p>
        </div>
        <Button
          type="button"
          onClick={handleSave}
          disabled={saving}
          data-testid="tank-config-save"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          Salvar tanques
        </Button>
      </div>
    </div>
  );
};

export default TankConfig;
